import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLES, ADMIN_VIEW_ROLES } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  BarChart3, Users, KanbanSquare, Webhook, Receipt, GraduationCap, LogOut, ChevronDown, KeyRound, Menu, SlidersHorizontal, Palette, Building2, PhoneCall, UsersRound, CalendarCheck, MessageCircle, ScrollText,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { Badge } from "../components/ui/badge";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { brandLogoUrl } from "../lib/theme";
import { IS_STANDALONE, INSTITUTION_NAME } from "../lib/deployment";

const NAV = [
  { label: "Analytics & Funnel", icon: BarChart3, route: "/analytics", roles: ["super_admin", "dean", "publisher", "finance"] },
  { label: "Counselor Reports", icon: PhoneCall, route: "/analytics/counselors", roles: ["super_admin", "dean"] },
  { label: "Leads Engine", icon: Users, route: "/leads", roles: ["super_admin", "dean", "counselor"] },
  { label: "Task Board", icon: CalendarCheck, route: "/tasks", roles: ["super_admin", "dean", "counselor"] },
  { label: "Application Pipeline", icon: KanbanSquare, route: "/applications", roles: ["super_admin", "dean", "counselor"] },
  { label: "Integrations & Webhooks", icon: Webhook, route: "/integrations", roles: ["super_admin", "publisher", "dean"] },
  { label: "Campaigns & Usage", icon: MessageCircle, route: "/campaigns/usage", roles: ["super_admin", "dean"] },
  { label: "Payments & GST", icon: Receipt, route: "/payments", roles: ["super_admin", "finance", "dean"] },
  { label: "Assignment Rules", icon: SlidersHorizontal, route: "/assignment-rules", roles: ["super_admin", "dean"] },
  { label: "Staff & Counselors", icon: UsersRound, route: "/staff", roles: ["super_admin", "dean"] },
  { label: "Audit Trail", icon: ScrollText, route: "/audit-trail", roles: ["super_admin", "dean"] },
  { label: "Institution & Branding", icon: Palette, route: "/institution-settings", roles: ["super_admin", "dean"] },
];

const ROLE_COLORS = {
  super_admin: "brand-active",
  dean: "bg-blue-100 text-blue-800",
  counselor: "bg-emerald-100 text-emerald-800",
  publisher: "bg-indigo-100 text-indigo-800",
  finance: "bg-teal-100 text-teal-800",
};

function SidebarBody({ items, onNavigate, institution }) {
  // tenant logo wins; otherwise the default EduPulse brand mark; GraduationCap
  // is the last-resort fallback if even that fails to load.
  const logo = brandLogoUrl(institution?.branding) || "/logo.png";
  const name = institution?.name || "EduPulse";
  return (
    <div className="flex h-full flex-col bg-white">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-200">
        <img src={logo} alt={name} className="h-9 w-9 rounded-lg object-contain shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "flex"; }} />
        <div className="w-9 h-9 rounded-lg brand-fill items-center justify-center shrink-0"
          style={{ display: "none" }}>
          <GraduationCap className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-slate-900 leading-tight truncate">{name}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Admissions CRM</div>
        </div>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {items.map((item) => (
          <NavLink
            key={item.route}
            to={item.route}
            end={item.route === "/analytics"}
            onClick={onNavigate}
            data-testid={`nav-${item.route.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "brand-active"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            <item.icon className="w-4 h-4 shrink-0" />
            <span className="flex-1 truncate">{item.label}</span>
            {item.badge ? (
              <span
                data-testid={`nav-badge-${item.route.slice(1)}`}
                className="ml-auto inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-bold text-white"
              >
                {item.badge > 99 ? "99+" : item.badge}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-200">
        <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Institution</div>
        <div className="text-sm font-medium text-slate-700 truncate">{name}</div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, institution, logout, viewRole, setViewRole, impersonation, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [taskAlerts, setTaskAlerts] = useState(0);

  const canSeeTasks = ["super_admin", "dean", "counselor"].includes(viewRole);
  useEffect(() => {
    if (!canSeeTasks) { setTaskAlerts(0); return undefined; }
    let alive = true;
    const load = () =>
      api.get("/tasks/summary")
        .then((r) => { if (alive) setTaskAlerts((r.data?.sla_breaches || 0) + (r.data?.overdue || 0)); })
        .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [canSeeTasks, viewRole, user?.institution_id]);

  let items = NAV.filter((n) => n.roles.includes(viewRole)).map((n) =>
    n.route === "/tasks" && taskAlerts > 0 ? { ...n, badge: taskAlerts } : n
  );
  // Platform-owner-only, and deliberately not part of NAV's roles-filtered
  // list above — there is no tenant `role` value that grants this, only
  // the real user's own is_platform_admin flag, so a tenant's own
  // super_admin (e.g. Aurora's demo admin, is_platform_admin: false) can
  // never see this link regardless of their tenant-level role. Also
  // hidden while impersonating a tenant, since the impersonated session's
  // is_platform_admin is that tenant's own (false) — see routes_auth.
  // require_platform_owner's docstring.
  if (user?.is_platform_admin && !impersonation && !IS_STANDALONE) {
    items = [{ label: "SaaS Control Panel", icon: Building2, route: "/platform/tenants" }, ...items];
  }
  const roleLabel = ROLES.find((r) => r.id === viewRole)?.label || viewRole;
  // Only an actual super_admin/dean account may simulate a different role
  // — everyone else's viewRole is already clamped to their own real role
  // (AuthContext), this just keeps the UI honest about it: no dropdown
  // offering roles a counselor/publisher/finance account could never
  // actually have.
  const canSwitchRole = ADMIN_VIEW_ROLES.includes(user?.role);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="app-layout">
      {impersonation && !IS_STANDALONE && (
        <div className="bg-amber-500 text-white text-sm px-4 py-2 flex items-center justify-center gap-3 flex-wrap" data-testid="impersonation-banner">
          <span>Viewing <strong>{impersonation.institution?.name}</strong> as platform admin{impersonation.acting_as ? ` (${impersonation.acting_as})` : ""}</span>
          <button onClick={async () => { await exitImpersonation(); navigate("/platform/tenants"); }}
            className="underline font-semibold" data-testid="exit-impersonation">Exit</button>
        </div>
      )}
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 border-r border-slate-200 flex-col z-30">
        <SidebarBody items={items} institution={institution} />
      </aside>

      {/* Mobile sidebar drawer */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-64 p-0" data-testid="mobile-sidebar">
          <SidebarBody items={items} institution={institution} onNavigate={() => setNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="lg:ml-64 min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between gap-3 px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              data-testid="mobile-nav-toggle"
              onClick={() => setNavOpen(true)}
              className="lg:hidden -ml-1 p-2 rounded-md text-slate-600 hover:bg-slate-100"
              aria-label="Open navigation"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="text-sm text-slate-500 truncate" data-testid="header-tenant-tag">
              {IS_STANDALONE ? (
                <span className="font-medium text-slate-700 truncate">{institution?.name || INSTITUTION_NAME}</span>
              ) : (
                <>
                  <span className="hidden md:inline">Multi-tenant lead engine <span className="mx-2 text-slate-300">|</span></span>
                  <span className="font-mono text-xs text-slate-400">{user?.institution_id || "—"}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {canSwitchRole ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="role-switcher-dropdown"
                    className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold ${ROLE_COLORS[viewRole] || "bg-slate-100 text-slate-700"}`}
                  >
                    <span className="hidden sm:inline">Viewing as:</span>
                    <span className="max-w-[6rem] sm:max-w-none truncate">{roleLabel}</span>
                    <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Simulate RBAC role</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ROLES.map((r) => (
                    <DropdownMenuItem
                      key={r.id}
                      data-testid={`role-option-${r.id}`}
                      onClick={() => setViewRole(r.id)}
                    >
                      {r.label}
                      {r.id === viewRole && <Badge className="ml-auto" variant="secondary">active</Badge>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              // Not an admin-tier account — show the same badge, purely
              // informational, with no dropdown affordance at all (no
              // other role is ever reachable from here for this account).
              <span
                data-testid="role-badge-readonly"
                className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold ${ROLE_COLORS[viewRole] || "bg-slate-100 text-slate-700"}`}
              >
                <span className="max-w-[8rem] sm:max-w-none truncate">{roleLabel}</span>
              </span>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-button"
                  className="w-9 h-9 rounded-full bg-brand text-white text-sm font-bold flex items-center justify-center shrink-0"
                >
                  {(user?.name || "U").charAt(0)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="change-password-menu-item"
                  onClick={() => setPwOpen(true)}
                >
                  <KeyRound className="w-4 h-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={async () => { await logout(); navigate("/login"); }}
                >
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

        <main className="p-4 sm:p-6 overflow-x-clip">{children}</main>
      </div>
    </div>
  );
}
